import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getRecommendations } from '../../../../helpers/api';

export default function RecommendationList({
  facilityId,
  quarter,
  year,
  showTitle = true,
  showFacilityName = false,
  refreshTrigger,
}) {
  const [recommendationList, setRecommendationList] = useState([]);
  const [recommendationLoading, setRecommendationLoading] = useState(false);

  useEffect(() => {
    if (facilityId && quarter && year) {
      fetchRecommendations();
    }
  }, [facilityId, quarter, year, refreshTrigger]);

  const fetchRecommendations = async () => {
    setRecommendationLoading(true);
    try {
      const res = await getRecommendations(facilityId, quarter, year);
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setRecommendationList(data || []);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setRecommendationLoading(false);
    }
  };

  const isContentEmpty = (htmlString) => {
    if (!htmlString) return true;
    const plainText = htmlString.replace(/<[^>]*>/g, '').trim();
    return !plainText;
  };

  return (
    <Box sx={{ width: '100%', my: 2 }}>
      {showTitle && (
        <>
          <Typography variant="h5" component="h2" fontWeight="700" color="text.primary" mb={2}>
            Recommendations
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {recommendationLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={36} />
        </Box>
      ) : recommendationList.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            backgroundColor: '#fafafa',
            borderStyle: 'dashed',
          }}
        >
          <Typography color="text.secondary" fontWeight="500">
            No recommendations available.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          {recommendationList.map((item) => (
            <Card
              key={item.id}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Stack spacing={2}>
                  {/* Top Bar */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    {showFacilityName && item.facilityName ? (
                      <Typography
                        variant="h6"
                        component="div"
                        fontWeight="600"
                        color="text.primary"
                      >
                        {item.facilityName}
                      </Typography>
                    ) : (
                      <Box />
                    )}

                    <Chip
                      label={`Q${item.reportQuarter} ${item.reportYear}`}
                      size="small"
                      color="primary"
                      sx={{
                        fontWeight: 600,
                        borderRadius: '6px',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        color: 'primary.main',
                        border: '1px solid rgba(25, 118, 210, 0.2)',
                      }}
                    />
                  </Box>

                  {/* Comment Section */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontWeight: 700,
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Comment
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      component="div"
                      sx={{
                        '& p': { m: 0, lineHeight: 1.6 },
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: '#f8f9fa',
                        borderLeft: '3px solid #1976d2',
                      }}
                      dangerouslySetInnerHTML={{ __html: item.comment }}
                    />
                  </Box>

                  {/* Actions Section */}
                  {item.actions && !isContentEmpty(item.actions) && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 700,
                          color: 'text.secondary',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Actions
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        component="div"
                        sx={{
                          '& p': { m: 0, lineHeight: 1.6 },
                          p: 1.5,
                          borderRadius: 1.5,
                          backgroundColor: '#f8f9fa',
                          borderLeft: '3px solid #2e7d32',
                        }}
                        dangerouslySetInnerHTML={{ __html: item.actions }}
                      />
                    </Box>
                  )}

                  {/* Footer Metadata Section */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    pt={1}
                    sx={{ borderTop: '1px solid', borderColor: 'grey.100' }}
                  >
                    {item.updateDate ? (
                      /* Show ONLY Last Updated if updateDate exists */
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                          Last updated by {item.updatedByFullName} on{' '}
                          {new Intl.DateTimeFormat('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }).format(new Date(item.updateDate))}
                        </Typography>
                      </Stack>
                    ) : (
                      /* Show ONLY Submitted by when no updateDate exists */
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          Submitted by:
                        </Typography>
                        <Typography variant="caption" color="text.primary" fontWeight="600">
                          {item.fullName}
                        </Typography>
                        {item.emailAddress && (
                          <Typography variant="caption" color="text.secondary">
                            ({item.emailAddress})
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
